// The level builder's data side: the level being edited, the current tool, placing and removing
// pieces and scenery models, ready-made obstacles, copy and paste, picking with a ray, and undo.
// No DOM or three.js, so it can be tested.

import { prefab, type Module } from '../maps/boxlevel/builtin';
import type { Shape } from '../maps/boxlevel/shapes';
import {
  DEFAULT_THEME,
  UPDOWN_TRAVEL,
  type BoxKind,
  type LevelBox,
  type LevelDef,
  type LevelProp,
  type LevelTheme,
  type MaterialName,
  type Vec3Tuple,
} from '../maps/boxlevel/types';

export const PALETTE = [
  '#ffffff',
  '#e8e8e8',
  '#9aa0a8',
  '#5a6070',
  '#2e333c',
  '#c76a2c',
  '#e04f5f',
  '#ff6f91',
  '#ff3fb4',
  '#9b5cff',
  '#3a8fd8',
  '#21e1ff',
  '#5ad1c8',
  '#5fae3e',
  '#a5d84a',
  '#f2c94c',
  '#ffb84d',
  '#a9744a',
  '#7a5230',
  '#d9b27a',
];

/** Kinds the builder can place; the first seven on keys 1..7. */
export const PLACEABLE: BoxKind[] = [
  'block',
  'solid',
  'checkpoint',
  'start',
  'end',
  'boost',
  'deco',
  'pad',
  'ladder',
  'water',
  'wind',
  'gravity',
];

/** Kinds you can walk into (not solid): they may overlap other pieces. */
export const PASS_THROUGH = new Set<BoxKind>(['deco', 'water', 'wind', 'gravity']);

export const KIND_LABEL: Record<BoxKind, string> = {
  block: 'Bhop block',
  solid: 'Solid',
  checkpoint: 'Checkpoint',
  start: 'Start',
  end: 'End',
  boost: 'Booster',
  deco: 'Scenery (no collision)',
  pad: 'Launch pad',
  ladder: 'Ladder',
  water: 'Water',
  wind: 'Wind',
  gravity: 'Low gravity',
};

export const SHAPE_ORDER: Shape[] = ['box', 'cylinder', 'hex', 'oct', 'tri', 'diamond', 'ramp'];

export type MovePreset = 'none' | 'side' | 'forward' | 'updown';
export const MOVE_PRESETS: { id: MovePreset; label: string }[] = [
  { id: 'none', label: 'Still' },
  { id: 'side', label: 'Slide sideways' },
  { id: 'forward', label: 'Slide back and forth' },
  { id: 'updown', label: 'Up and down' },
];

const sky = (name: string, o: Partial<LevelTheme>) => ({ name, theme: { ...DEFAULT_THEME, ...o } });
export const THEMES: { name: string; theme: LevelTheme }[] = [
  sky('Day', {}),
  sky('Morning', { skyTop: '#6fa8dc', skyHorizon: '#ffe0b8', fog: '#f5dcc0', voidColor: '#7a9a6a' }),
  sky('Sunset', { skyTop: '#6b3b7a', skyHorizon: '#ffb26b', fog: '#f0a070', voidColor: '#a65a33' }),
  sky('Dusk', { skyTop: '#1d1a4a', skyHorizon: '#c0607a', fog: '#7a4a6a', voidColor: '#2a1a3a' }),
  sky('Night', { skyTop: '#05030f', skyHorizon: '#2a0f45', fog: '#1a0b2e', voidColor: '#0b0614' }),
  sky('Space', { skyTop: '#000004', skyHorizon: '#0c1a3a', fog: '#060c1c', voidColor: '#04060c' }),
  sky('Snow', { skyTop: '#9fc4e8', skyHorizon: '#eef6ff', fog: '#e4efff', voidColor: '#dbe8f5' }),
  sky('Ocean', { skyTop: '#1d5fa8', skyHorizon: '#a8e6ff', fog: '#8fd0f0', voidColor: '#1c6e9e' }),
  sky('Jungle', { skyTop: '#2f6b3a', skyHorizon: '#d6f0b0', fog: '#b9d99a', voidColor: '#1f4a24' }),
  sky('Candy', { skyTop: '#ff9ad5', skyHorizon: '#fff1c9', fog: '#ffe6f2', voidColor: '#8fd4ff' }),
  sky('Hell', { skyTop: '#140202', skyHorizon: '#7a1a08', fog: '#3a0a04', voidColor: '#ff5a10' }),
  sky('Toxic', { skyTop: '#0a2a0a', skyHorizon: '#7aff5a', fog: '#3a7a2a', voidColor: '#1a3a10' }),
];

export type ToolMode = 'piece' | 'prop' | 'prefab' | 'paste';

export interface Tool {
  mode: ToolMode;
  kind: BoxKind;
  shape: Shape;
  turn: number;
  material: MaterialName;
  color: string;
  /** Piece size in units (X, Y, Z). */
  size: Vec3Tuple;
  /** Snap grid in units. */
  grid: number;
  move: MovePreset;
  /** Travel distance and time of moving pieces. */
  moveDist: number;
  movePeriod: number;
  /** Scenery model id and its scale. */
  model: string;
  modelScale: number;
  /** Ready-made obstacle. */
  prefab: Module;
}

export interface Hit {
  type: 'box' | 'prop';
  index: number;
  t: number;
  point: Vec3Tuple;
  /** Face normal for boxes (one axis, +1 or -1); up for props. */
  normal: Vec3Tuple;
}

export interface Clipboard {
  boxes: LevelBox[];
  props: LevelProp[];
  /** Size of the copied group (for the preview). */
  size: Vec3Tuple;
}

/** Default size for each kind when you switch to it. */
const KIND_SIZE: Record<BoxKind, Vec3Tuple> = {
  block: [128, 128, 24],
  solid: [256, 256, 32],
  checkpoint: [320, 320, 64],
  start: [512, 512, 64],
  end: [512, 512, 64],
  boost: [192, 192, 32],
  deco: [128, 128, 128],
  pad: [160, 160, 24],
  ladder: [16, 96, 384],
  water: [512, 512, 256],
  wind: [256, 256, 512],
  gravity: [512, 512, 512],
};

const KIND_LOOK: Partial<Record<BoxKind, [MaterialName, string]>> = {
  checkpoint: ['grid', '#5f6f82'],
  start: ['grid', '#5d7563'],
  end: ['grid', '#7a5a5a'],
  boost: ['neon', '#ffd23a'],
  pad: ['neon', '#3affb0'],
  ladder: ['ladder', '#c89a5a'],
  water: ['water', '#2a8fc8'],
  wind: ['wind', '#9ad8ff'],
  gravity: ['wind', '#c39bff'],
};

export function newLevel(name: string, title: string): LevelDef {
  const box = (
    min: Vec3Tuple,
    max: Vec3Tuple,
    kind: BoxKind,
    material: MaterialName,
    color: string,
  ): LevelBox => ({
    min,
    max,
    kind,
    material,
    color,
  });
  return {
    v: 1,
    name,
    title,
    difficulty: 1,
    theme: { ...DEFAULT_THEME },
    boxes: [
      box([-256, -256, -64], [256, 256, 0], 'start', 'grid', '#5d7563'),
      box([400, -64, -24], [528, 64, 0], 'block', 'grid', '#c76a2c'),
      box([660, -64, -24], [788, 64, 0], 'block', 'grid', '#c76a2c'),
      box([920, -64, -24], [1048, 64, 0], 'block', 'grid', '#c76a2c'),
      box([1180, -256, -64], [1692, 256, 0], 'end', 'grid', '#7a5a5a'),
    ],
    props: [],
    spawn: { pos: [-150, 0, 0.03125], yaw: 0 },
    failZ: -600,
  };
}

/** Ray against an axis-aligned box (slab test). Returns entry distance and face, or null. */
function rayBox(o: readonly number[], d: readonly number[], min: readonly number[], max: readonly number[]) {
  let tMin = -Infinity;
  let tMax = Infinity;
  let axis = -1;
  let sign = 0;
  for (let k = 0; k < 3; k++) {
    if (Math.abs(d[k]) < 1e-9) {
      if (o[k] < min[k] || o[k] > max[k]) return null;
      continue;
    }
    let t1 = (min[k] - o[k]) / d[k];
    let t2 = (max[k] - o[k]) / d[k];
    let s = -1;
    if (t1 > t2) {
      [t1, t2] = [t2, t1];
      s = 1;
    }
    if (t1 > tMin) {
      tMin = t1;
      axis = k;
      sign = s;
    }
    tMax = Math.min(tMax, t2);
    if (tMin > tMax) return null;
  }
  if (tMax < 0 || axis < 0 || tMin < 0) return null;
  return { t: tMin, axis, sign };
}

const snap = (v: number, g: number) => Math.round(v / g) * g + 0;
const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v)) as T;

/** Rough size of a scenery model at scale 1 (Kenney models are about a metre). */
const PROP_RADIUS = 0.6;

export class EditorModel {
  def: LevelDef;
  tool: Tool = {
    mode: 'piece',
    kind: 'block',
    shape: 'box',
    turn: 0,
    material: 'grid',
    color: '#c76a2c',
    size: [...KIND_SIZE.block] as Vec3Tuple,
    grid: 16,
    move: 'none',
    moveDist: 192,
    movePeriod: 3,
    model: 'tree_default',
    modelScale: 80,
    prefab: 'zigzag',
  };
  clipboard: Clipboard | null = null;
  /** Pieces picked while holding C (indices into def.boxes / def.props). */
  readonly selection = { boxes: new Set<number>(), props: new Set<number>() };
  private undoStack: string[] = [];
  private redoStack: string[] = [];
  /** Changed since the last save. */
  dirty = false;
  /** Bumped on every change (views rebuild when it changes). */
  version = 0;

  constructor(def: LevelDef) {
    this.def = clone(def);
    this.def.props ??= [];
  }

  private snapshot() {
    this.undoStack.push(JSON.stringify(this.def));
    if (this.undoStack.length > 200) this.undoStack.shift();
    this.redoStack.length = 0;
  }

  /** `keep`: the edit didn't add or remove pieces, so the selection still points at the same ones. */
  private changed(keep = false) {
    this.dirty = true;
    this.version++;
    if (!keep) {
      this.selection.boxes.clear();
      this.selection.props.clear();
    }
    this.updateSpawn();
  }

  undo(): boolean {
    const s = this.undoStack.pop();
    if (!s) return false;
    this.redoStack.push(JSON.stringify(this.def));
    this.def = JSON.parse(s) as LevelDef;
    this.changed();
    return true;
  }

  redo(): boolean {
    const s = this.redoStack.pop();
    if (!s) return false;
    this.undoStack.push(JSON.stringify(this.def));
    this.def = JSON.parse(s) as LevelDef;
    this.changed();
    return true;
  }

  /* ---------------- Tool ---------------- */

  setKind(kind: BoxKind) {
    this.tool.mode = 'piece';
    this.tool.kind = kind;
    this.tool.size = [...KIND_SIZE[kind]] as Vec3Tuple;
    const look = KIND_LOOK[kind];
    if (look) [this.tool.material, this.tool.color] = look;
    if (kind === 'start' || kind === 'end' || kind === 'checkpoint') {
      this.tool.shape = 'box';
      this.tool.move = 'none';
    }
  }

  setShape(shape: Shape) {
    this.tool.mode = 'piece';
    this.tool.shape = shape;
  }

  cycleShape(step: number) {
    const i = SHAPE_ORDER.indexOf(this.tool.shape);
    this.setShape(SHAPE_ORDER[(i + step + SHAPE_ORDER.length) % SHAPE_ORDER.length]);
  }

  rotate() {
    this.tool.turn = (this.tool.turn + 1) % 4;
    if (this.tool.mode !== 'piece') return;
    // Ramps and triangles turn; swap the footprint so it turns with them.
    const s = this.tool.size;
    [s[0], s[1]] = [s[1], s[0]];
  }

  setMaterial(m: MaterialName) {
    this.tool.material = m;
    if (this.tool.mode !== 'piece' && this.tool.mode !== 'prefab') this.tool.mode = 'piece';
  }

  setColor(c: string) {
    this.tool.color = c;
  }

  cycleColor(step: number) {
    const i = PALETTE.indexOf(this.tool.color);
    this.tool.color = PALETTE[(Math.max(0, i) + step + PALETTE.length) % PALETTE.length];
  }

  /** Grow or shrink along an axis by one grid step (min one step, max 4096). For props: scale. */
  resize(axis: 0 | 1 | 2, steps: number) {
    if (this.tool.mode === 'prop') {
      this.tool.modelScale = Math.max(
        8,
        Math.min(1000, Math.round(this.tool.modelScale * (1 + 0.08 * steps))),
      );
      return;
    }
    const g = this.tool.grid;
    this.tool.size[axis] = Math.max(g, Math.min(4096, this.tool.size[axis] + steps * g));
  }

  cycleGrid() {
    const grids = [8, 16, 32, 64, 128];
    this.tool.grid = grids[(grids.indexOf(this.tool.grid) + 1) % grids.length];
  }

  cycleMove() {
    const i = MOVE_PRESETS.findIndex((m) => m.id === this.tool.move);
    this.tool.move = MOVE_PRESETS[(i + 1) % MOVE_PRESETS.length].id;
  }

  setProp(model: string) {
    this.tool.mode = 'prop';
    this.tool.model = model;
  }

  setPrefab(m: Module) {
    this.tool.mode = 'prefab';
    this.tool.prefab = m;
  }

  setTheme(i: number) {
    this.snapshot();
    this.def.theme = { ...THEMES[((i % THEMES.length) + THEMES.length) % THEMES.length].theme };
    this.changed();
  }

  themeIndex(): number {
    return THEMES.findIndex((t) => t.theme.skyTop === this.def.theme.skyTop);
  }

  /* ---------------- Picking and placing ---------------- */

  /** First piece or scenery model the ray hits. `dir` must be normalised. */
  pick(origin: readonly number[], dir: readonly number[], maxDist = 6000): Hit | null {
    let best: Hit | null = null;
    this.def.boxes.forEach((b, i) => {
      const h = rayBox(origin, dir, b.min, b.max);
      if (!h || h.t > maxDist || (best && h.t >= best.t)) return;
      const normal: Vec3Tuple = [0, 0, 0];
      normal[h.axis] = h.sign;
      best = { type: 'box', index: i, t: h.t, point: this.along(origin, dir, h.t), normal };
    });
    (this.def.props ?? []).forEach((p, i) => {
      const r = p.scale * PROP_RADIUS;
      const min = [p.pos[0] - r, p.pos[1] - r, p.pos[2]];
      const max = [p.pos[0] + r, p.pos[1] + r, p.pos[2] + r * 2.5];
      const h = rayBox(origin, dir, min, max);
      if (!h || h.t > maxDist || (best && h.t >= best.t)) return;
      best = { type: 'prop', index: i, t: h.t, point: this.along(origin, dir, h.t), normal: [0, 0, 1] };
    });
    return best;
  }

  private along(o: readonly number[], d: readonly number[], t: number): Vec3Tuple {
    return [o[0] + d[0] * t, o[1] + d[1] * t, o[2] + d[2] * t];
  }

  /** The point a placement hangs off: the aimed face (and its normal), or a point ahead. */
  aim(
    origin: readonly number[],
    dir: readonly number[],
    reach = 500,
  ): { point: Vec3Tuple; normal: Vec3Tuple; hit: Hit | null } {
    const hit = this.pick(origin, dir);
    if (hit && hit.type === 'box') return { point: hit.point, normal: hit.normal, hit };
    if (hit) return { point: hit.point, normal: [0, 0, 1], hit };
    return { point: this.along(origin, dir, reach), normal: [0, 0, 1], hit: null };
  }

  /**
   * Where a piece of `size` goes: against the face you aim at (on top of a piece, beside it), or
   * floating ahead. Snapped to the grid.
   */
  ghost(origin: readonly number[], dir: readonly number[], size: Vec3Tuple = this.tool.size, reach = 500) {
    const g = this.tool.grid;
    const { point, normal, hit } = this.aim(origin, dir, reach);
    const min: Vec3Tuple = [0, 0, 0];
    const b = hit?.type === 'box' ? this.def.boxes[hit.index] : null;
    for (let k = 0; k < 3; k++) {
      if (b && normal[k] > 0) min[k] = b.max[k];
      else if (b && normal[k] < 0) min[k] = b.min[k] - size[k];
      else if (!b && k === 2 && hit) min[k] = point[2];
      else min[k] = snap(point[k] - size[k] / 2, g);
    }
    return { min, max: [min[0] + size[0], min[1] + size[1], min[2] + size[2]] as Vec3Tuple };
  }

  private overlaps(min: readonly number[], max: readonly number[]): boolean {
    return this.def.boxes.some(
      (b) => !PASS_THROUGH.has(b.kind) && [0, 1, 2].every((k) => min[k] < b.max[k] && b.min[k] < max[k]),
    );
  }

  /** Motion for a new piece from the tool, along `yaw` for "back and forth". */
  private motion(yaw: number): LevelBox['move'] | undefined {
    const t = this.tool;
    if (t.move === 'none' || t.kind === 'start' || t.kind === 'end' || t.kind === 'checkpoint')
      return undefined;
    const d = t.moveDist;
    const fx = Math.round(Math.cos(yaw)) * d;
    const fy = Math.round(Math.sin(yaw)) * d;
    const offset: Vec3Tuple =
      t.move === 'updown'
        ? [0, 0, -UPDOWN_TRAVEL]
        : t.move === 'forward'
          ? [fx + 0, fy + 0, 0]
          : [0 - fy, fx + 0, 0];
    return { offset, period: t.movePeriod, phase: 0 };
  }

  /** Place the tool piece. Only one start and one end: placing one moves it. */
  place(min: Vec3Tuple, max: Vec3Tuple, yaw = 0): string | null {
    const t = this.tool;
    if (!PASS_THROUGH.has(t.kind) && this.overlaps(min, max)) return 'Something is already there.';
    if (this.def.boxes.length >= 5000) return 'That is a lot of pieces: the limit is 5000.';
    this.snapshot();
    if (t.kind === 'start' || t.kind === 'end')
      this.def.boxes = this.def.boxes.filter((b) => b.kind !== t.kind);
    const box: LevelBox = {
      min: [...min],
      max: [...max],
      kind: t.kind,
      material: t.material,
      color: t.color,
    };
    if (t.shape !== 'box') box.shape = t.shape;
    if (t.turn) box.turn = t.turn;
    if (t.kind === 'boost')
      box.boost = [Math.round(Math.cos(yaw) * 300), Math.round(Math.sin(yaw) * 300), 200];
    else if (t.kind === 'pad') box.boost = [0, 0, 800];
    else if (t.kind === 'wind')
      box.boost = [Math.round(Math.cos(yaw) * 600), Math.round(Math.sin(yaw) * 600), 0];
    else if (t.kind === 'gravity') box.gravity = 0.5;
    const move = this.motion(yaw);
    if (move) box.move = move;
    this.def.boxes.push(box);
    this.changed();
    this.lastPlaced = this.def.boxes.length - 1;
    return null;
  }

  /** Index of the piece placed last (the builder can select it). */
  lastPlaced = -1;

  placeProp(at: Vec3Tuple, yaw: number): string | null {
    const props = (this.def.props ??= []);
    if (props.length >= 3000) return 'The limit is 3000 scenery models.';
    this.snapshot();
    props.push({
      model: this.tool.model,
      pos: [...at],
      yaw: (this.tool.turn * Math.PI) / 2 + yaw,
      scale: this.tool.modelScale,
    });
    this.changed();
    return null;
  }

  /** The pieces a ready-made obstacle would add at `at` heading along `yaw` (preview and placing). */
  prefabPieces(at: Vec3Tuple, yaw: number): LevelBox[] {
    const t = this.tool;
    const look: [MaterialName, string] = [t.material, t.color];
    return prefab(t.prefab, at, yaw, Math.max(64, Math.min(256, t.size[0])), look, look, this.version + 1);
  }

  placePrefab(at: Vec3Tuple, yaw: number): string | null {
    const pieces = this.prefabPieces(at, yaw);
    if (pieces.some((p) => !PASS_THROUGH.has(p.kind) && this.overlaps(p.min, p.max)))
      return 'Not enough room there.';
    this.snapshot();
    this.def.boxes.push(...pieces);
    this.changed();
    return null;
  }

  /** Remove what the hit points at. The start and the end can only be moved, not removed. */
  remove(hit: Hit): string | null {
    if (hit.type === 'prop') {
      this.snapshot();
      this.def.props?.splice(hit.index, 1);
      this.changed();
      return null;
    }
    const b = this.def.boxes[hit.index];
    if (!b) return null;
    if (b.kind === 'start' || b.kind === 'end')
      return `The ${b.kind} can't be removed; place a new one to move it.`;
    this.snapshot();
    this.def.boxes.splice(hit.index, 1);
    this.changed();
    return null;
  }

  /** Copy the look and size of what the hit points at into the tool. */
  eyedrop(hit: Hit) {
    if (hit.type === 'prop') {
      const p = this.def.props?.[hit.index];
      if (p) {
        this.setProp(p.model);
        this.tool.modelScale = p.scale;
      }
      return;
    }
    const b = this.def.boxes[hit.index];
    if (!b) return;
    this.tool.mode = 'piece';
    this.tool.kind = b.kind;
    this.tool.shape = b.shape ?? 'box';
    this.tool.turn = b.turn ?? 0;
    this.tool.material = b.material;
    this.tool.color = b.color;
    this.tool.size = [b.max[0] - b.min[0], b.max[1] - b.min[1], b.max[2] - b.min[2]];
  }

  /* ---------------- Selection (select tool) ---------------- */

  get selectedCount(): number {
    return this.selection.boxes.size + this.selection.props.size;
  }

  /** Select just what the hit points at (nothing for null). */
  selectOnly(hit: Hit | null) {
    this.selection.boxes.clear();
    this.selection.props.clear();
    if (hit) this.select(hit);
  }

  /** Add or remove what the hit points at. */
  toggleSelect(hit: Hit) {
    const set = hit.type === 'box' ? this.selection.boxes : this.selection.props;
    if (set.has(hit.index)) set.delete(hit.index);
    else set.add(hit.index);
  }

  isSelected(hit: Hit): boolean {
    return (hit.type === 'box' ? this.selection.boxes : this.selection.props).has(hit.index);
  }

  /** Bounds of everything selected, or null. */
  selectionBounds(): { min: Vec3Tuple; max: Vec3Tuple } | null {
    if (!this.selectedCount) return null;
    const lo: Vec3Tuple = [Infinity, Infinity, Infinity];
    const hi: Vec3Tuple = [-Infinity, -Infinity, -Infinity];
    const grow = (a: readonly number[], b: readonly number[]) => {
      for (let k = 0; k < 3; k++) {
        lo[k] = Math.min(lo[k], a[k]);
        hi[k] = Math.max(hi[k], b[k]);
      }
    };
    for (const i of this.selection.boxes) grow(this.def.boxes[i].min, this.def.boxes[i].max);
    for (const i of this.selection.props) {
      const p = this.def.props![i];
      const rr = p.scale * PROP_RADIUS;
      grow([p.pos[0] - rr, p.pos[1] - rr, p.pos[2]], [p.pos[0] + rr, p.pos[1] + rr, p.pos[2] + rr * 2.5]);
    }
    return { min: lo, max: hi };
  }

  /** The single selected piece, if exactly one piece (not scenery) is selected. */
  selectedBox(): LevelBox | null {
    if (this.selection.boxes.size !== 1 || this.selection.props.size) return null;
    return this.def.boxes[[...this.selection.boxes][0]] ?? null;
  }

  /** Move everything selected by `d` (one undo step). */
  moveSelection(d: readonly number[]) {
    if (!this.selectedCount || (!d[0] && !d[1] && !d[2])) return;
    this.snapshot();
    for (const i of this.selection.boxes) {
      const b = this.def.boxes[i];
      for (let k = 0; k < 3; k++) {
        b.min[k] += d[k];
        b.max[k] += d[k];
      }
    }
    for (const i of this.selection.props) {
      const p = this.def.props![i];
      for (let k = 0; k < 3; k++) p.pos[k] += d[k];
    }
    this.changed(true);
  }

  /** Change the selected pieces (one undo step); `fn` edits each box in place. */
  editSelected(fn: (b: LevelBox) => void): string | null {
    if (!this.selection.boxes.size) return null;
    this.snapshot();
    for (const i of this.selection.boxes) {
      const b = this.def.boxes[i];
      fn(b);
      for (let k = 0; k < 3; k++) {
        b.min[k] = Math.round(b.min[k] * 1000) / 1000;
        b.max[k] = Math.round(b.max[k] * 1000) / 1000;
        if (b.max[k] - b.min[k] < 1) b.max[k] = b.min[k] + 1;
      }
      // Start, end and checkpoints are plain still boxes.
      if (b.kind === 'start' || b.kind === 'end' || b.kind === 'checkpoint') {
        delete b.move;
        delete b.shape;
        delete b.turn;
      }
      if (b.kind === 'boost') b.boost ??= [300, 0, 200];
      else if (b.kind === 'pad') b.boost ??= [0, 0, 800];
      else if (b.kind === 'wind') b.boost ??= [600, 0, 0];
      else delete b.boost;
      if (b.kind === 'gravity') b.gravity ??= 0.5;
      else delete b.gravity;
      // Fields, water and ladders stay put.
      if (PASS_THROUGH.has(b.kind) || b.kind === 'ladder') {
        delete b.spin;
        delete b.blink;
      }
    }
    // Only one start and one end: if this made another, the old one goes.
    let removed = false;
    for (const kind of ['start', 'end'] as const) {
      const all = this.def.boxes.map((b, i) => [b, i] as const).filter(([b]) => b.kind === kind);
      if (all.length < 2) continue;
      const keep = all.find(([, i]) => this.selection.boxes.has(i)) ?? all[all.length - 1];
      this.def.boxes = this.def.boxes.filter((b) => b.kind !== kind || b === keep[0]);
      removed = true;
    }
    this.changed(!removed);
    return null;
  }

  /** Remove what's selected (the start and end stay). */
  deleteSelection(): string | null {
    if (!this.selectedCount) return null;
    const boxes = [...this.selection.boxes].filter((i) => {
      const k = this.def.boxes[i]?.kind;
      return k !== 'start' && k !== 'end';
    });
    const keptSpecial = boxes.length < this.selection.boxes.size;
    if (!boxes.length && !this.selection.props.size)
      return "The start and the end can't be removed; move them instead.";
    this.snapshot();
    const dropB = new Set(boxes);
    const dropP = new Set(this.selection.props);
    this.def.boxes = this.def.boxes.filter((_, i) => !dropB.has(i));
    this.def.props = (this.def.props ?? []).filter((_, i) => !dropP.has(i));
    this.changed();
    return keptSpecial ? "Removed. (The start and the end can't be removed.)" : null;
  }

  /** Copy what's selected in place and select the copies (move them next). */
  duplicateSelection(): string | null {
    const boxes = [...this.selection.boxes]
      .map((i) => this.def.boxes[i])
      .filter((b) => b && b.kind !== 'start' && b.kind !== 'end');
    const props = [...this.selection.props].map((i) => this.def.props![i]).filter(Boolean);
    if (!boxes.length && !props.length)
      return 'Select something to duplicate (the start and end are one of a kind).';
    if (this.def.boxes.length + boxes.length > 5000) return 'That is a lot of pieces: the limit is 5000.';
    this.snapshot();
    const b0 = this.def.boxes.length;
    const p0 = (this.def.props ??= []).length;
    this.def.boxes.push(...boxes.map((b) => clone(b)));
    this.def.props.push(...props.map((p) => clone(p)));
    this.changed();
    for (let i = 0; i < boxes.length; i++) this.selection.boxes.add(b0 + i);
    for (let i = 0; i < props.length; i++) this.selection.props.add(p0 + i);
    return null;
  }

  /* ---------------- Copy and paste ---------------- */

  /** Add what the hit points at to the copy selection (while C is held). */
  select(hit: Hit) {
    (hit.type === 'box' ? this.selection.boxes : this.selection.props).add(hit.index);
  }

  /** Copy the selection (or just `hit` if nothing is selected) and switch to pasting. */
  copy(hit: Hit | null): string | null {
    if (hit && !this.selection.boxes.size && !this.selection.props.size) this.select(hit);
    const boxes = [...this.selection.boxes]
      .map((i) => this.def.boxes[i])
      .filter((b) => b && b.kind !== 'start' && b.kind !== 'end');
    const props = [...this.selection.props]
      .map((i) => this.def.props?.[i])
      .filter((p): p is LevelProp => !!p);
    this.selection.boxes.clear();
    this.selection.props.clear();
    if (!boxes.length && !props.length)
      return 'Nothing to copy: aim at something (hold C and sweep over pieces to copy several).';
    // Relative to the group's bottom centre.
    const lo = [Infinity, Infinity, Infinity];
    const hi = [-Infinity, -Infinity, -Infinity];
    for (const b of boxes)
      for (let k = 0; k < 3; k++) {
        lo[k] = Math.min(lo[k], b.min[k]);
        hi[k] = Math.max(hi[k], b.max[k]);
      }
    for (const p of props)
      for (let k = 0; k < 3; k++) {
        lo[k] = Math.min(lo[k], p.pos[k]);
        hi[k] = Math.max(hi[k], p.pos[k]);
      }
    const ox = (lo[0] + hi[0]) / 2;
    const oy = (lo[1] + hi[1]) / 2;
    const oz = lo[2];
    const rel = (v: readonly number[]): Vec3Tuple => [v[0] - ox, v[1] - oy, v[2] - oz];
    this.clipboard = {
      boxes: boxes.map((b) => ({ ...clone(b), min: rel(b.min), max: rel(b.max) })),
      props: props.map((p) => ({ ...clone(p), pos: rel(p.pos) })),
      size: [hi[0] - lo[0], hi[1] - lo[1], hi[2] - lo[2]],
    };
    this.tool.mode = 'paste';
    return null;
  }

  /** The clipboard moved so its bottom centre sits at `at` (snapped to the grid). */
  pasted(at: Vec3Tuple): { boxes: LevelBox[]; props: LevelProp[] } | null {
    const c = this.clipboard;
    if (!c) return null;
    const g = this.tool.grid;
    const o: Vec3Tuple = [snap(at[0], g), snap(at[1], g), at[2]];
    const add = (v: readonly number[]): Vec3Tuple => [v[0] + o[0], v[1] + o[1], v[2] + o[2]];
    return {
      boxes: c.boxes.map((b) => ({ ...clone(b), min: add(b.min), max: add(b.max) })),
      props: c.props.map((p) => ({ ...clone(p), pos: add(p.pos) })),
    };
  }

  paste(at: Vec3Tuple): string | null {
    const p = this.pasted(at);
    if (!p) return 'Nothing copied yet: aim at something and press C.';
    if (p.boxes.some((b) => !PASS_THROUGH.has(b.kind) && this.overlaps(b.min, b.max)))
      return 'Not enough room there.';
    this.snapshot();
    this.def.boxes.push(...p.boxes);
    (this.def.props ??= []).push(...p.props);
    this.changed();
    return null;
  }

  /* ---------------- Level ---------------- */

  /** Spawn on the start platform, fail height under the lowest piece. */
  private updateSpawn() {
    const start = this.def.boxes.find((b) => b.kind === 'start');
    if (start) {
      this.def.spawn = {
        pos: [(start.min[0] + start.max[0]) / 2, (start.min[1] + start.max[1]) / 2, start.max[2] + 0.03125],
        yaw: this.def.spawn.yaw,
      };
    }
    const minZ = Math.min(...this.def.boxes.map((b) => b.min[2]));
    this.def.failZ = minZ - 400;
  }

  rename(title: string) {
    this.def.title = title.trim().slice(0, 60) || 'Untitled';
    this.dirty = true;
  }
}
